/**
 * Help Center: "payments" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'how-installments-work',
    title: 'How installment payments work',
    summary: 'You pay a deposit when you book, or $0 with Defera Zero. The rest is charged automatically, weekly or bi-weekly, to your card until your trip is paid off.',
    keywords: ['payment plan', 'deposit', 'pay over time', 'layaway', 'autopay', 'weekly payments', 'bi-weekly payments', 'paid in full'],
    related: ['payments/payment-frequency', 'payments/view-payment-schedule', 'getting-started/installments-vs-zero', 'payments/pay-off-early'],
    updated: '2026-10-05',
    body: `You pay for your trip over time. When you book, you pay a deposit, or nothing at all with Defera Zero, and the rest is charged automatically to your card, weekly or bi-weekly, until your trip is paid in full.

There's no credit check and no interest, and you can pay early whenever you like, with no penalty. Your trip price is protected once your booking is approved, so as long as you keep up with your payments, a rise in airfares won't raise your price.

## How it works, step by step

Here's what happens, from checkout to your final payment:

1. **You choose a plan at checkout.** Defera Installments is the default, and it works on any fare on the airlines we support. Defera Zero, with nothing due when you book, is offered on refundable fares from {{ZERO_AIRLINES}}.
2. **You pay your deposit.** On Defera Installments, deposits start as low as 5% on trips more than 90 days away. Your deposit options depend on how far away your trip is, and you'll see them at checkout. On Defera Zero there's no deposit, and your first payment is due on your first scheduled payment date.
3. **Your trip price is secured.** Once your booking is approved, your agreed-upon trip price is protected from future airfare increases. It stays protected as long as you're in good standing, which means keeping up with your payment plan.
4. **Your payments run on their own.** The rest of your trip total is charged automatically to your card on file, on the weekly or bi-weekly schedule you chose. You don't need to remember to pay.
5. **Your trip is paid in full.** After your final payment goes through, our team releases your airline confirmation number to your trip in **Trips**, usually within 24 hours on weekdays.

## What you'll see at checkout

Before you pay anything, your checkout page lays out your plan in this order:

- what's due today;
- how many weekly or bi-weekly payments follow, how much each one is, and the date of the last one;
- your trip total, including taxes and fees.

For example, with made-up numbers, you might see "8 bi-weekly payments of $95 · last on March 3" under the amount due today.

The **Price Breakdown** on the same page shows that your trip total already includes the [service fee and the hold fee](/help/payments/fees-explained), so no extra fees are added later.

## What "card on file" means

Your card on file is the debit or credit card saved to your plan when you book. Your payments are charged to it automatically on their dates, which is why every plan needs one.

We accept [debit and credit cards](/help/payments/accepted-payment-methods). Prepaid cards, including gift cards, aren't accepted on installment plans. If your card changes later, you can replace it from your trip with **Update Card**.

## How far ahead do I need to book?

Every Defera trip departs at least {{PLAN_MIN_DAYS}} days after you book. Closer dates can't be booked at all, even if you'd like to pay the full price up front.

## Do Flight + Hotel trips work the same way?

Yes. Flight + Hotel packages are paid with Defera Installments, and your deposit and payments cover the combined flight and hotel total. Defera Zero isn't available on Flight + Hotel packages.

## Can my plan change later?

Yes, your schedule can change with you. Depending on your plan, you can [change your payment schedule](/help/payments/change-schedule) in a few ways:

- switch between weekly and bi-weekly payments, with nothing charged today;
- pick your own payment dates, for a {{DATE_CHANGE_FEE}} fee that covers every change you make that day;
- pay ahead, which moves your next automatic charge back;
- skip your next payment, for a {{SKIP_FEE}} fee.

The one schedule that isn't offered is monthly.

## When do I get my confirmation number?

Your airline confirmation number is the code the airline uses to find your reservation, and you'll use it to check in. It's released once your trip is paid in full. Until then, it's completely normal for your trip to show a payment schedule and no confirmation number.

Paying off early gets it to you sooner, and there's no penalty for paying early. Open your trip in **Trips**, tap **Make a Payment** and choose **Pay off**.`,
  },
  {
    slug: 'payment-frequency',
    title: 'Weekly or bi-weekly payments',
    summary: 'Your plan runs weekly or bi-weekly; there’s no monthly option. If your trip shows Change Schedule, you can switch between the two for free.',
    keywords: ['monthly payments', 'payment frequency', 'every two weeks', 'biweekly', 'how often', 'switch frequency', 'pay monthly'],
    related: ['payments/change-schedule', 'payments/pick-your-dates', 'payments/make-a-payment', 'payments/how-installments-work'],
    updated: '2026-10-05',
    body: `Defera payment plans run on one of two schedules: weekly, or bi-weekly (every two weeks). You choose yours at checkout. If your trip shows **Change Schedule**, you can switch between the two later, with no fee and nothing charged today.

Monthly payments aren't offered. If you'd like to pay less often, you can still set your own rhythm with **Pay ahead**, which we explain below.

## How do weekly and bi-weekly compare?

The difference is how often you pay, and how much each payment is:

| | Weekly | Bi-weekly |
|---|---|---|
| How often you're charged | Every week | Every two weeks |
| Size of each payment | Smaller | Larger |
| Number of payments | More | Fewer |

If the size of each payment matters most to you, weekly keeps each one smaller. If you'd rather have fewer charges, bi-weekly does that.

Either way, every payment is charged automatically to your card on file, the debit or credit card saved to your plan. Before you book, your checkout page shows how many payments you'll make, how much each one is and the date of the last one.

## Can I switch later?

Yes, if your trip shows **Change Schedule**. It's free, and nothing is charged today.

1. Open **Trips** and tap your trip.
2. In the Payment Schedule section, tap **Change Schedule**.
3. Compare your current and new dates and amounts, side by side.
4. If the new schedule works for you, confirm.

When you switch, your remaining balance is spread across the new schedule, and every future date is recalculated. Payments you've already made stay as they are.

For example, say you have $600 left to pay on a bi-weekly plan (an example amount). If you switch to weekly, that $600 is spread across more, smaller payments, one every week. The side-by-side preview shows your exact new amounts and dates before you confirm, so you'll know what to expect.

## What if I don't see Change Schedule?

**Change Schedule** is available on Defera Zero and on newer Defera Installments plans. On some older Defera Installments plans, the same spot shows **Request Extension** instead, which lets you [move your final payment date later](/help/payments/extend-final-payment) for a {{EXTENSION_FEE}} fee.

## Can I pay monthly?

Monthly payments aren't offered, but you can create a similar rhythm with **Pay ahead**, inside **Make a Payment**. Pay ahead lets you pay your next installments today, and your next automatic charge moves back to match.

For example, on a bi-weekly plan, you could use Pay ahead to pay two installments at once. That covers about four weeks, and your card isn't charged again until the payment after those two comes due.

Pay ahead means paying more today, so it suits a moment when you have the money on hand. If a payment is going to be hard to make, look at [your options when you can't pay on time](/help/missed-payments/cant-pay-on-time) instead.

## Need a payment on a particular date?

On plans that offer it, **Change Schedule** also includes **Pick my own dates**. It lets you [choose the date of your next payment](/help/payments/pick-your-dates), for a {{DATE_CHANGE_FEE}} fee that covers every date change you make that day.`,
  },
  {
    slug: 'view-payment-schedule',
    title: 'Viewing your payment schedule',
    summary: 'Open Trips and tap your trip. The Payment Schedule section shows your payments and holds the buttons to pay, change your schedule and update your card.',
    keywords: ['next payment', 'due date', 'when is my payment', 'installment dates', 'payment history', 'upcoming payments', 'how much do I owe'],
    related: ['payments/trip-statement', 'payments/make-a-payment', 'payments/balance-explained', 'missed-payments/past-due-balance'],
    updated: '2026-10-05',
    body: `Your payment schedule lives on your trip page. Open **Trips**, tap your trip, and look for the Payment Schedule section. On a computer it's on the left, and on your phone it's at the top.

Think of that section as the home base for your plan. It shows your payments, and it holds the buttons you use to pay, update your card and change your schedule.

## How do I find it?

1. Sign in and open [Trips](/trips).
2. Tap the trip you want to check.
3. Find the Payment Schedule section: on the left on a computer, or at the top on your phone.

If you have more than one trip, each one has its own payment schedule. Open each trip to see its payments.

## What can I do from it?

Each button in the Payment Schedule section does one job:

- **Make a Payment:** pay ahead, pay any amount toward your balance with **Principal**, pay off your whole balance, or catch up on a past-due payment.
- **Update Card:** replace the card your payments are charged to.
- **Change Schedule:** switch between weekly and bi-weekly payments, or pick your own payment dates on plans that offer it.
- **Skip Payment:** make your next installment $0, for a {{SKIP_FEE}} fee.
- **Apply Credits:** put Defera Credit you received after booking toward your upcoming payments.

Which buttons you see depends on your plan. On some older Defera Installments plans, for example, **Request Extension** appears where **Change Schedule** would be, and **Make a Payment** opens a separate payment page.

## Why does my schedule look the way it does?

A few things can change how your schedule looks:

- **Defera Credit is used first.** Defera Credit is credit on your Defera account that you can put toward trips with us. If some is applied to your trip, it covers your earliest payments first, so those can drop to $0. Your card is charged again from the first payment the credit doesn't cover.
- **Extra payments change it.** **Pay ahead** moves your next automatic charge back. A **Principal** payment makes your future installments smaller, and the dates stay the same.
- **Skipping changes the amounts, not the dates.** After **Skip Payment**, your next installment is $0, and the ones after it rise slightly.

For example, if $100 of Defera Credit is applied to your trip and your weekly payments are $50 (example amounts), your next two payments are covered and show $0. Your card is charged again from the third. If your balance doesn't seem to match, here's [how Defera Credit and your balance fit together](/help/payments/balance-explained).

## What do the banners and cards mean?

- **A past-due banner.** It means a payment didn't go through. You can [catch up on what's past due](/help/missed-payments/past-due-balance) from **Make a Payment**, and you don't have to pay it all at once.
- **A paused plan.** A plan left past due for a long time can pause. If that happens, your trip shows **Restart Plan**, which gets your payments going again.
- **A release card.** Once your final payment goes through, a "Confirmation Number Release" card appears on your trip page while our team releases your confirmation number. If you don't see it, tap **Finished paying for your trip?** in your payment schedule.

## Where can I see my full payment history?

Download your [Trip Statement](/help/payments/trip-statement). It's a PDF with your plan's full history, including paid, upcoming and any past-due installments, and you'll find it under the download icon in your trip's header row.`,
  },
  {
    slug: 'automatic-payments',
    title: 'Are payments automatic?',
    summary: 'Yes. Each payment is charged to your card on file on its scheduled date, so there’s nothing to remember. Here’s what changes if you pay early or a payment fails.',
    keywords: ['autopay', 'auto charge', 'recurring payments', 'card on file', 'automatic charge', 'charged automatically', 'pay manually'],
    related: ['payments/update-card', 'payments/make-a-payment', 'missed-payments/payment-failed', 'missed-payments/cant-pay-on-time'],
    updated: '2026-10-05',
    body: `Yes. After you book, each payment is charged automatically to your card on file on its scheduled date, weekly or bi-weekly, until your trip is paid in full.

You don't need to do anything for a scheduled payment to go through. The one thing to keep an eye on is your card: if it expires or gets replaced, update it on your trip so your payments keep going through.

## What does "card on file" mean?

Your card on file is the debit or credit card saved to your payment plan. Every plan needs one, because your payments are charged automatically on their schedule.

That's also why checkout asks for a card even when Defera Credit covers everything due today. The card is saved for your future payments, and it's charged $0 at booking when credit covers what's due.

## How do I change my card?

1. Open **Trips** and tap your trip.
2. In the Payment Schedule section, tap **Update Card**.
3. Enter the new card's details and save.

From then on, the new card is used for all your future payments. If a new card won't save, [Updating your card](/help/payments/update-card) has what to try.

## What happens if I pay before a payment is due?

You can pay early at any time from **Make a Payment** on your trip, with no fee and no penalty. What happens to your automatic payments afterward depends on the option you choose:

| Option | What happens to your automatic payments |
|---|---|
| **Pay ahead** | Your next one or more payments are paid today, and your next automatic charge moves back |
| **Principal** | Your future payments get smaller, and their dates stay the same |
| **Pay off** | Your whole remaining balance is paid, so there are no more automatic charges |

In short: Pay ahead gives you breathing room later, Principal makes each future payment smaller, and Pay off finishes your plan.

For example, if your weekly payment is due this Friday and you cover it with Pay ahead on Monday, your card isn't charged on Friday. Your next automatic charge is the following Friday's payment.

## What if a payment doesn't go through?

If an [automatic payment fails](/help/missed-payments/payment-failed), your trip isn't cancelled on the spot. Here's what happens:

1. **Your trip shows a past-due banner.** The missed amount stays past due until it's paid, and it may be retried on your card.
2. **You catch up when you can.** Tap **Make a Payment** and pay what's past due. You don't have to pay it all at once.
3. **Your plan picks up again.** Once nothing is past due, your payments continue on their normal schedule.

If your card is the problem, **Update Card** is in the same section. A plan left past due for a long time can pause; if that happens, your trip shows **Restart Plan** to get your payments going again.

## What if an upcoming payment looks hard?

It's worth [looking at your options](/help/missed-payments/cant-pay-on-time) before the payment is due. Depending on your plan, you can skip your next payment for a {{SKIP_FEE}} fee, or switch to smaller weekly payments with nothing charged today.`,
  },
  {
    slug: 'make-a-payment',
    title: 'Making an extra payment',
    summary: 'Tap Make a Payment on your trip to pay ahead, pay toward your balance with Principal, or pay off your trip. None of them has a fee or a penalty.',
    keywords: ['pay early', 'pay extra', 'pay ahead', 'custom payment', 'extra payment', 'pay more', 'payment now'],
    related: ['payments/pay-off-early', 'payments/automatic-payments', 'missed-payments/past-due-balance', 'missed-payments/cant-pay-on-time'],
    updated: '2026-10-05',
    body: `You can pay more than your scheduled amount at any time. Open **Trips**, tap your trip, and tap **Make a Payment** in the Payment Schedule section.

There are three ways to pay extra, and none of them has a fee or a penalty. The main thing to decide is what you'd like to happen afterward: more time before your next charge, smaller payments from here on, or no more payments at all.

## What are my options?

When nothing is past due, **Make a Payment** gives you three tabs. Each one costs something different today, and changes something different afterward.

### Pay ahead

**Pay ahead** is the tab that opens by default. It pays your next one or more installments today.

- **What you pay today:** the installments you choose to cover.
- **What changes afterward:** your next automatic charge moves back, giving you breathing room later.

For example, say your weekly payment is $50 and your next payments are due on the 7th and the 14th (example figures). If you pay ahead two installments, you pay $100 today, both of those payments are covered, and your next automatic charge is on the 21st.

Inside Pay ahead, **Custom amount** lets you enter an amount of your own. It pays toward your upcoming installments, and it moves your next charge back too.

### Principal

**Principal** pays any amount you choose toward your balance. (This tab used to be called "Custom".)

- **What you pay today:** any amount you choose.
- **What changes afterward:** your future installments get smaller, and their dates stay the same. Your next payment is still due on the same day, for less.

For example, say you have $600 left to pay and you pay $150 with Principal (example figures). Your balance drops to $450, your payment dates don't move, and your future payments get smaller to match.

### Pay off

**Pay off** clears your entire remaining balance in one go.

- **What you pay today:** everything that's left. If Defera Credit is applied to your trip, Pay off charges only what's left after that credit, and the credit covers the rest.
- **What changes afterward:** there are no more payments. Your trip is paid in full, and our team releases your airline confirmation number.

Here's [what happens after you pay off your trip](/help/payments/pay-off-early), including when to expect your confirmation number.

## Which one should I choose?

- **You have money now and want room later:** choose **Pay ahead**.
- **You'd like smaller payments from here on:** choose **Principal**.
- **You want to be done, and get your confirmation number:** choose **Pay off**.

All three mean paying more today. If an upcoming payment is going to be hard to make, they're not the right tool: [skipping a payment or switching to weekly](/help/missed-payments/cant-pay-on-time) can help instead.

## How do I make a payment?

1. Open **Trips** and tap your trip.
2. In the Payment Schedule section, tap **Make a Payment**.
3. Choose **Pay ahead**, **Principal** or **Pay off**.
4. Check the amount, then confirm.

## What if a payment is past due?

If a payment didn't go through, **Make a Payment** puts it first. A red banner at the top asks you to pay what's past due, and the other options open once it's cleared.

You don't have to [clear it all at once](/help/missed-payments/past-due-balance). The **Past due** tab takes any amount from $5 toward your oldest missed payment, with your card on file or a new card.

## What if my screen looks different?

On some older Defera Installments plans, **Make a Payment** opens a separate payment page instead. There, you can pay an extra installment, pay off your balance or clear a past-due payment.`,
  },
  {
    slug: 'pay-off-early',
    title: 'Paying off your trip early',
    summary: 'Tap Make a Payment, then Pay off, to clear your balance in one go. There’s no penalty, and your confirmation number comes once your trip is paid in full.',
    keywords: ['pay in full', 'payoff', 'pay off balance', 'early payoff', 'pay remaining balance', 'confirmation number sooner', 'finish paying'],
    related: ['payments/make-a-payment', 'confirmation-and-travel/when-confirmation-number', 'confirmation-and-travel/paid-off-no-confirmation', 'emails/paid-off-email'],
    updated: '2026-10-05',
    body: `You can pay off your trip early, at any time, with no fee and no penalty. Open your trip, tap **Make a Payment** and choose **Pay off** to clear your whole remaining balance in one go.

Paying off early doesn't lower your trip total, because there's no interest to save. What it changes is timing: your airline confirmation number is released once your trip is paid in full, so paying off early gets it to you sooner.

## How do I pay off my trip?

1. Open **Trips** and tap your trip.
2. In the Payment Schedule section, tap **Make a Payment**.
3. Choose **Pay off**.
4. Check the amount, then confirm.

If a payment is past due, you'll pay that first, and the other options open once it's cleared. On some older Defera Installments plans, **Make a Payment** opens a separate payment page, where you can pay off your balance.

## Why is my payoff amount lower than my balance?

If Defera Credit is applied to your trip, that credit is already set aside to cover your upcoming payments. So **Pay off** charges only what's left after the credit, and the credit covers the rest.

For example, say your remaining balance is $500, and $200 of Defera Credit is applied to your trip (example amounts). Pay off charges $300 to your card, and your credit covers the other $200.

## What happens after my final payment?

You don't need to do anything else. Here's what happens:

1. **Your payoff goes through.** A "Confirmation Number Release" request is sent to our team for you automatically. You'll see it as a card on your trip page.
2. **Our team releases your confirmation number.** They work Monday through Friday, Pacific time.
3. **We email you.** When your confirmation number is ready, you'll get a "Your trip has been paid off!" email. Tap **Get Confirmation Number** in the email to see it, or open **Trips** and tap your trip. If your trip earned Defera Miles, the email tells you how many.
4. **The airline emails you too.** You'll also hear from the airline directly, at the email address on your booking. From there, you check in with the airline as usual.

## How long does it take?

It depends on when your payoff goes through:

- **Monday through Thursday, or Friday morning (Pacific time):** expect your confirmation number within 24 hours.
- **Friday afternoon or over the weekend:** expect it on Monday, or Tuesday at the latest.

For example, if you pay off your trip on a Wednesday morning, you can expect your confirmation number by Thursday morning. If you pay it off on a Saturday, look for it on Monday.

## What if it's taking longer?

If it's been longer than that, or you don't see the release card on your trip page, open your trip in **Trips** and tap **Finished paying for your trip?** in the payment schedule. It sends the request if one wasn't sent, or confirms that yours is already being handled.

If you're still waiting after that, [chat with the Defera Concierge](/support), our AI concierge. It can check on your trip and send a request to our team. [I've paid off my trip. Where's my confirmation number?](/help/confirmation-and-travel/paid-off-no-confirmation) walks through this step by step.`,
  },
  {
    slug: 'change-schedule',
    title: 'Changing your payment schedule',
    summary: 'Switch between weekly and bi-weekly for free, pick your own dates, pay ahead or skip a payment. Here’s what each option costs today and what it changes.',
    keywords: ['change payment date', 'move a payment', 'reschedule payment', 'push payment back', 'adjust schedule', 'payment options', 'smaller payments'],
    related: ['payments/pick-your-dates', 'payments/skip-a-payment', 'payments/payment-frequency', 'missed-payments/cant-pay-on-time'],
    updated: '2026-10-05',
    body: `Your payment schedule can change with you. Depending on your plan, you can switch between weekly and bi-weekly payments for free, pick your own payment dates for {{DATE_CHANGE_FEE}}, pay ahead to push your next charge back, or skip your next payment for {{SKIP_FEE}}.

You can make each of these changes yourself from your trip page, in a few taps. The one thing that isn't offered is a monthly schedule.

## What does each option cost today?

Some options cost nothing today, and some mean paying something now. Here's how they compare:

| Option | What it does | Cost today |
|---|---|---|
| **Change Schedule** | Switches between weekly and bi-weekly, and re-spreads your balance | Nothing |
| **Pick my own dates** | Lets you choose your next payment date | {{DATE_CHANGE_FEE}}, covering every change you make that day |
| **Can't make your payment?** | Moves a payment you can't make up to {{MOVE_MAX_DAYS}} days later | Nothing. The Extension Fee ({{MOVE_FEE_SHORT}} or {{MOVE_FEE_LONG}}) is spread across your remaining payments |
| **Pay ahead** | Pays your next installments now, and moves your next charge back | The installments you pay |
| **Skip Payment** | Makes your next installment $0, and the rest rise slightly | {{SKIP_FEE}} |
| **Request Extension** (some older Defera Installments plans) | Moves your final payment date later | {{EXTENSION_FEE}} |

If money is tight right now, start with the first row: switching schedules costs nothing today. Which options you see depends on your plan.

## How do I switch between weekly and bi-weekly?

If your trip shows **Change Schedule**:

1. Open **Trips** and tap your trip.
2. In the Payment Schedule section, tap **Change Schedule**.
3. Review the side-by-side preview of your current and new dates and amounts.
4. Confirm.

Your remaining balance is spread across the new schedule, and every future date is recalculated. There's no fee, and nothing is charged today. Weekly payments are smaller and more frequent, which can help when the size of each payment is the hard part.

## How do I pick my own dates?

On plans that offer it, **Change Schedule** also includes **Pick my own dates**. You choose the date of your next payment, and {{DATE_CHANGE_FEE}} is charged to your card on file when you confirm.

That fee works like a day pass: it covers every date change you make that day. For example, if your payment is due on the 3rd but you get paid on the 5th, you can move it to the 5th. If you change your mind that afternoon, changing it again costs nothing extra.

Two conditions apply:

- **Departure cutoff.** Every new date must be at least {{DATE_CHANGE_CUTOFF_DAYS}} days before your departure.
- **Nothing past due.** Anything past due has to be paid first.

[Picking your own payment dates](/help/payments/pick-your-dates) has the full details.

## How do Pay ahead and Skip Payment work?

**Pay ahead** is inside **Make a Payment**. It pays your next one or more installments today, and your next automatic charge moves back. Because it means paying money now, it suits a moment when you have cash on hand and want breathing room later.

**Skip Payment** is its own button in the Payment Schedule section, on Defera Installments plans that show it. Your next installment becomes $0, and the {{SKIP_FEE}} fee is charged to your card on file today. Your remaining installments go up slightly to cover the skipped amount, and your payment dates don't move.

For example, on a weekly plan with payments every Friday, skipping this Friday's payment means your next charge is the following Friday. The Skip Payment screen shows your exact new amounts before you confirm, and there's a [worked example of skipping a payment](/help/payments/skip-a-payment) if you'd like to see the math.

## What if I don't see Change Schedule?

**Change Schedule** is available on Defera Zero and newer Defera Installments plans. On some older Defera Installments plans, the same spot shows **Request Extension** instead, which lets you [move your final payment date later](/help/payments/extend-final-payment) for a {{EXTENSION_FEE}} fee.

## Which option should I use?

- **Have money now and want room later?** Use **Pay ahead**.
- **Finding each payment too big?** Switch to weekly with **Change Schedule**. Nothing is charged today.
- **Payday doesn't line up with your payment date?** Use **Pick my own dates**.
- **Can't make the next payment?** **Can't make your payment?** moves it up to {{MOVE_MAX_DAYS}} days later, with nothing charged today. [Moving a payment you can't make](/help/payments/move-a-payment) explains the fee. Or **Skip Payment** makes it $0 for {{SKIP_FEE}}.

If you're worried about an upcoming payment, [I can't make my next payment](/help/missed-payments/cant-pay-on-time) talks through every option.`,
  },
  {
    slug: 'pick-your-dates',
    title: 'Picking your own payment dates',
    summary: 'Choose your next payment date with Pick my own dates, inside Change Schedule. It costs {{DATE_CHANGE_FEE}}, and one fee covers every change you make that day.',
    keywords: ['choose payment date', 'move payment date', 'change due date', 'payday', 'specific date', 'reschedule a payment', 'date change fee'],
    related: ['payments/change-schedule', 'payments/skip-a-payment', 'payments/fees-explained', 'missed-payments/past-due-balance'],
    updated: '2026-10-05',
    body: `You can choose the date of your next payment yourself. If your trip shows **Change Schedule**, open it and choose **Pick my own dates**. It costs {{DATE_CHANGE_FEE}}, charged to your card on file when you confirm, and that one fee covers every date change you make that day.

This is handy when your payment date doesn't line up with your payday. You can move it to a date that works better, without needing to ask us first.

## How do I pick my dates?

1. Open **Trips** and tap your trip.
2. In the Payment Schedule section, tap **Change Schedule**.
3. Choose **Pick my own dates**, then select your new date.
4. Review the change and confirm. {{DATE_CHANGE_FEE}} is charged to your card on file.

For example, if your next payment is due on the 3rd and your paycheck arrives on the 5th, you can move the payment to the 5th. Once you confirm, it's charged on the 5th instead.

On plans that show this option, you can also set the exact date and amount of each of your remaining installments.

## What does it cost?

It costs {{DATE_CHANGE_FEE}}, and the fee works like a day pass: one fee covers every date change you make that day.

For example, say you move your payment at noon, then realize at 4 p.m. that a different date works better. You can change it again at 4 p.m. without paying a second time. A change on another day is a new {{DATE_CHANGE_FEE}}.

So if you're unsure which date is best, you can take your time and settle it today, without paying again.

## Are there any limits?

Two conditions apply:

- **Departure cutoff.** Every new date must be at least {{DATE_CHANGE_CUTOFF_DAYS}} days before your departure.
- **Nothing past due.** Anything past due has to be paid first. If a payment didn't go through, [catch up on what's past due](/help/missed-payments/past-due-balance) from **Make a Payment**, then pick your dates.

## What if I need breathing room, not a new date?

There are [other ways to adjust your schedule](/help/payments/change-schedule):

- **Switch between weekly and bi-weekly.** Use **Change Schedule** itself. It's free: your balance re-spreads, and nothing is charged today.
- **Pay ahead.** In **Make a Payment**, Pay ahead pushes your next automatic charge back, but it means paying now.
- **Skip a payment.** On Defera Installments plans that show **Skip Payment**, your next installment becomes $0 for {{SKIP_FEE}}, and your remaining payments rise slightly.

## What if I don't see Pick my own dates?

**Pick my own dates** is inside **Change Schedule**, on plans that offer it. On some older Defera Installments plans, **Request Extension** appears where **Change Schedule** would be.

If you're not sure what your plan offers, [chat with the Defera Concierge](/support), our AI concierge. It can tell you what's available on your trip and walk you through it.`,
  },
  {
    slug: 'move-a-payment',
    title: 'Moving a payment you can’t make',
    summary: 'Pick the day you’ll be able to pay, up to {{MOVE_MAX_DAYS}} days later. The Extension Fee rides on your remaining payments, so nothing is charged today.',
    keywords: ['can’t make my payment', 'more time to pay', 'push payment back', 'payday', 'extension fee', 'missed payment', 'delay a payment', 'pay later'],
    related: ['missed-payments/cant-pay-on-time', 'payments/change-schedule', 'payments/skip-a-payment', 'missed-payments/past-due-balance'],
    updated: '2026-10-25',
    body: `If you can't make a payment, you can move it to the day you'll be able to pay, up to {{MOVE_MAX_DAYS}} days later. On your trip, tap **Can't make your payment?** under the Payment Schedule buttons and pick the day. The Extension Fee is {{MOVE_FEE_SHORT}} for up to {{MOVE_FEE_SHORT_DAYS}} days later, or {{MOVE_FEE_LONG}} for up to {{MOVE_MAX_DAYS}}, and it's spread across your remaining payments. Nothing is charged today.

It works for a payment that's coming up, and for one that already didn't go through.

## How do I move a payment?

1. Open **Trips** and tap your trip.
2. Under the Payment Schedule buttons, tap **Can't make your payment?** If a payment didn't go through, it reads **Missed a payment? Pick a day that works**.
3. Pick the day you'll be able to pay.
4. Check your new schedule: the new date, what each payment will be, and how many are left.
5. Tick the box to agree to the Extension Fee, then tap **Move my payment**.

## What does it cost?

| If you move it | The Extension Fee is |
|---|---|
| Up to {{MOVE_FEE_SHORT_DAYS}} days later | {{MOVE_FEE_SHORT}} |
| Up to {{MOVE_MAX_DAYS}} days later | {{MOVE_FEE_LONG}} |

The fee isn't charged on its own. It's added across your remaining payments, so each one goes up a little, and nothing is charged the day you move it. The screen shows your exact new amounts before you agree.

The fee can't be waived, and it isn't refunded, even if you cancel your trip later: a cancellation's credit or refund leaves it out of what you've paid.

## What happens to my other payments?

Your later payments move with it and keep the same spacing. For example, say you pay every two weeks and your payment on the 1st is moved to the 8th. Your next one moves from the 15th to the 22nd, and so on. A payment never moves earlier than the date it already had.

Your payments still finish at least {{DATE_CHANGE_CUTOFF_DAYS}} days before departure. If the last ones wouldn't fit, they're combined into the payments before them, and the screen shows that before you agree.

## Can I move a payment that already didn't go through?

Yes. If a payment didn't go through, pick a day up to {{MOVE_MAX_DAYS}} days from today. We stop retrying it, the missed payment moves to the day you picked, and your trip is back on track. If we've already booked another try for it, pick a day after that try.

If your bank won't approve the card again (for example, it was reported lost or it expired), your trip asks you to update your card first, and then you can move the payment.

## Are there any limits?

- **Once per payment.** A payment you've moved can't be moved again.
- **{{MOVES_PER_TRIP}} per trip.** You can move a payment {{MOVES_PER_TRIP}} times on each trip.
- **Before departure.** Payments finish at least {{DATE_CHANGE_CUTOFF_DAYS}} days before you fly.

## Is this a late fee?

No. Nothing is ever charged for a missed payment on its own. The Extension Fee applies only when you choose to move a payment.

If a new date won't help, [I can't make my next payment](/help/missed-payments/cant-pay-on-time) covers your other options, like skipping a payment or switching to smaller weekly payments.`,
  },
  {
    slug: 'skip-a-payment',
    title: 'Skipping a payment',
    summary: 'Skip Payment makes your next installment $0 for a {{SKIP_FEE}} fee charged today. Your dates stay the same, and the payments after it rise slightly.',
    keywords: ['skip installment', 'miss a payment', 'skip fee', 'pause payment', 'postpone payment', 'skip next payment', 'delay a payment'],
    related: ['payments/change-schedule', 'payments/pick-your-dates', 'missed-payments/cant-pay-on-time', 'payments/fees-explained'],
    updated: '2026-10-05',
    body: `If your trip shows **Skip Payment**, you can skip your next installment. That payment becomes $0, a {{SKIP_FEE}} fee is charged to your card on file today, and your remaining installments go up slightly to cover the skipped amount. Your payment dates don't move.

Skipping is different from missing a payment. It's a planned change you make ahead of time, so the skipped payment doesn't go past due, and your plan carries on as normal.

## How do I skip a payment?

1. Open **Trips** and tap your trip.
2. In the Payment Schedule section, tap **Skip Payment**. It's its own button, not part of **Make a Payment**.
3. Check the new amount for your remaining payments. The Skip Payment screen shows the exact figure before you confirm anything.
4. Confirm. {{SKIP_FEE}} is charged to your card on file today.

## What changes, and what stays the same?

What changes:

- **Your next installment** becomes $0.
- **You pay {{SKIP_FEE}} today**, charged to your card on file.
- **Each remaining installment rises slightly.** Your remaining balance is divided across the payments that are left.

What stays the same is your payment dates, including your final payment date. So your next charge lands on the following scheduled date: one week later on a weekly plan, or two weeks later on a bi-weekly plan.

## Can you show me an example?

Here's how the numbers work, using example amounts:

1. **Before you skip.** You have $450 left to pay, in 10 weekly payments of $45, due every Friday.
2. **You skip this Friday's payment.** It becomes $0, and {{SKIP_FEE}} is charged to your card today.
3. **The rest are recalculated.** Your $450 is divided across the 9 payments that are left, so each one becomes $50.
4. **Your next charge.** Your card is next charged $50 on the following Friday.

Your own Skip Payment screen shows your exact new amount before you confirm, so you'll know the figure in advance.

## When can I skip?

- **Your plan needs to offer it.** Skip Payment is available on Defera Installments plans that show this option, for trips that aren't yet paid in full.
- **Nothing can be past due.** Skip Payment isn't available while a payment is past due. [Catch up on what's past due](/help/missed-payments/past-due-balance) from **Make a Payment** first, and it becomes available again.
- **The fee can't be waived.** The {{SKIP_FEE}} fee is charged to your card on file when you skip.

## Is skipping my best option?

It depends on what's making the payment hard. Skipping costs {{SKIP_FEE}} today, so it fits best when one particular payment is the problem.

- **If the size of each payment is the problem,** switching to weekly with **Change Schedule** makes each payment smaller, and nothing is charged today.
- **If the timing is the problem,** **Pick my own dates** can move your next payment to a date that works better, for {{DATE_CHANGE_FEE}}.

[I can't make my next payment](/help/missed-payments/cant-pay-on-time) compares all your options.`,
  },
  {
    slug: 'extend-final-payment',
    title: 'Extending your final payment date',
    summary: 'On some older plans, Request Extension moves your final payment date later for {{EXTENSION_FEE}}. On newer plans, you have other ways to get more time.',
    keywords: ['extension', 'more time to pay', 'extend my plan', 'push back last payment', 'final payment date', 'request extension', 'longer plan'],
    related: ['payments/change-schedule', 'payments/skip-a-payment', 'missed-payments/cant-pay-on-time', 'contact/how-requests-work'],
    updated: '2026-10-05',
    body: `On some older Defera Installments plans, a **Request Extension** button lets you move your final payment date later, for a {{EXTENSION_FEE}} fee. Your final payment date is the date of the last payment on your plan.

If your trip shows **Change Schedule** instead, there's no extension button. You still have ways to get breathing room, though: depending on your plan, you can switch to smaller weekly payments, pick your own payment dates or skip a payment.

## Which one does my trip have?

Open **Trips**, tap your trip, and look at the buttons in the Payment Schedule section:

- **If you see Request Extension,** read on. The next section explains how it works.
- **If you see Change Schedule,** skip ahead to "What if my trip shows Change Schedule?" below.

## How do I request an extension?

1. Open **Trips** and tap your trip.
2. In the Payment Schedule section, tap **Request Extension**.
3. Follow the steps on screen.

How far you can move your final payment date depends on how often you pay:

| Your plan | How far you can move your final payment date |
|---|---|
| Weekly | 1 to 4 weeks |
| Bi-weekly | 2 to 8 weeks |

So a bi-weekly plan has more room to move than a weekly one. For example, on a weekly plan with a final payment due on May 1, you could move it out by one to four weeks, so as late as May 29, as long as that stays within the limit ahead of your departure.

A few more things to know:

- **The fee:** {{EXTENSION_FEE}}.
- **A limit before you fly:** extensions are capped ahead of your departure date.
- **One at a time:** you can have one extension pending at a time.

## What if my trip shows Change Schedule?

There's no **Request Extension** on these plans, but you have [other ways to get breathing room](/help/payments/change-schedule):

- **Smaller payments.** If you pay bi-weekly, **Change Schedule** can switch you to weekly payments, so each one is smaller. There's no fee, and nothing is charged today.
- **A date that suits you.** **Pick my own dates**, inside **Change Schedule**, lets you choose your next payment date for {{DATE_CHANGE_FEE}}. Every new date must be at least {{DATE_CHANGE_CUTOFF_DAYS}} days before departure.
- **A skipped payment.** **Skip Payment**, on Defera Installments plans that show it, makes your next installment $0 for {{SKIP_FEE}}. Your remaining payments rise slightly, and your dates stay the same.

## What if none of these fit?

We know it's stressful when a payment date is coming up and none of the options quite fit. You can ask us for more time.

[Chat with the Defera Concierge](/support), our AI concierge, and it can send a request to our team. It helps to include:

- your booking reference;
- how much more time you're asking for;
- anything you'd like us to know about your situation.

Here's what happens next: your request shows as a card on your trip page, and our team's answer posts there and comes to you by email, usually within 24 hours. [How requests to our team work](/help/contact/how-requests-work) explains the process.`,
  },
  {
    slug: 'update-card',
    title: 'Updating your card',
    summary: 'Open Trips, tap your trip and tap Update Card. It’s the only place to change your card, and the new one is used for all your future payments.',
    keywords: ['change card', 'new card', 'card on file', 'expired card', 'payment method', 'update CVC', 'replace card', 'expiration date'],
    related: ['payments/accepted-payment-methods', 'payments/card-declined', 'payments/prepaid-cards', 'account/payment-security'],
    updated: '2026-10-05',
    body: `To change the card your payments are charged to, open **Trips**, tap your trip, and tap **Update Card** in the Payment Schedule section. It sits right next to **Make a Payment**. Enter the new card and save, and from then on it's used for all your future payments automatically.

This is the only place to update your card. There's no Payment Methods page in your Profile or anywhere else in the app, so if you've been looking for one, you haven't missed it.

## When should I update my card?

Your card on file is the card your payments are charged to. It's a good idea to update it when:

- your card has expired, or is about to;
- your bank sent you a replacement card, even if the number is the same;
- your card was lost or stolen;
- you'd like your payments to come from a different card.

## How do I update it?

1. Open **Trips** and tap your trip.
2. In the Payment Schedule section, tap **Update Card**.
3. Enter the full card details: card number, expiration date, CVC and ZIP code.
4. Save. The new card replaces the old one for your future payments.

**Update Card** does the same job on Defera Installments and Defera Zero. On some older Defera Installments plans, it opens a separate payment page where you enter the new card.

## Can I change only the expiration date or CVC?

Not on its own. For security, saved cards can't be edited in place. If only the expiration date or CVC changed, use **Update Card** and enter the full card again. It replaces the old card in one step.

For example, if your bank mails you a new card with the same number and a new expiration date, tap **Update Card** and enter the whole card, including the new expiration date and CVC.

## Which cards can I add?

You can add any [debit or credit card](/help/payments/accepted-payment-methods). Prepaid cards, including gift and stored-value cards, aren't accepted on installment plans.

## What if my new card won't save?

First, the reassuring part: a failed update changes nothing on your plan. The card already on file keeps being charged on schedule.

Here's what to try:

1. **Check the details.** The number, expiration date, CVC and ZIP code must match the card exactly.
2. **Try again, or use a different card.** Any other debit or credit card works.
3. **Check whether it's prepaid.** A prepaid card won't save, even if the message doesn't say why.

## What if a payment already failed on my old card?

Once your new card is saved, catch up on what's past due from **Make a Payment**. You don't have to pay it all at once. If you're not sure what went wrong with the old card, these are [common reasons a card is declined](/help/payments/card-declined).

## Is my card information safe?

We never store complete card details, and payments are processed through certified PCI-DSS payment gateways. [How we protect your payment details](/help/account/payment-security) explains more.`,
  },
  {
    slug: 'accepted-payment-methods',
    title: 'What payment methods can I use?',
    summary: 'We accept debit and credit cards on every plan. Bank transfers, PayPal, Venmo, Cash App, Apple Pay, Google Pay, checks and crypto aren’t accepted.',
    keywords: ['debit card', 'credit card', 'bank account', 'ACH', 'PayPal', 'Apple Pay', 'Venmo', 'Cash App', 'pay with bank'],
    related: ['payments/prepaid-cards', 'payments/update-card', 'booking/using-credit-at-checkout', 'rewards/using-miles-at-checkout'],
    updated: '2026-10-05',
    body: `Defera accepts debit and credit cards, on every plan. Bank transfers, PayPal, Venmo, Cash App, Apple Pay, Google Pay, checks and cryptocurrency aren't accepted.

If you'd like your payments to come from your bank account, there's an easy way to do it: use a debit card linked to that account, such as the one your bank already issued you.

## Why do I need a card?

Every plan needs a card on file: the debit or credit card your payments are charged to. Your payments are charged to it automatically on their schedule, so you don't have to remember to pay.

That's why checkout asks for a card, even when Defera Credit covers everything due today. The card is saved for your future payments, and it's charged $0 at booking when credit covers what's due.

## What do you accept?

Here's the full picture:

| Payment method | Accepted? |
|---|---|
| Debit cards | Yes |
| Credit cards | Yes |
| Prepaid, gift and stored-value cards | Not on installment plans |
| Bank account payments (ACH, direct debit, e-check) | No |
| Bank transfers and wires | No |
| PayPal, Venmo or Cash App | No |
| Apple Pay or Google Pay | No |
| Checks or cryptocurrency | No |

The short version: regular debit and credit cards are accepted, and other ways to pay aren't.

## Can I pay from my bank account?

Not directly. There's no way to pay from a checking account, no direct debit and no e-check. But any debit card drawn on that same bank account works, including the card your bank already issued you.

For example, if you'd like your payments to come out of your checking account, add the debit card for that account at checkout, or later with **Update Card**. Your payments are then charged to that card.

## What about prepaid and gift cards?

Prepaid cards, including [gift cards and other stored-value cards](/help/payments/prepaid-cards), aren't accepted on installment plans. The decline doesn't always say why, so if a card keeps failing and it's prepaid, switch to a regular debit or credit card.

## Are there other ways to lower what I pay?

Yes. These aren't payment methods, but they can bring the price of your trip down at checkout. Any payments left on your plan are still charged to your card on file.

- **Defera Credit.** Credit on your Defera account [applies automatically at checkout](/help/booking/using-credit-at-checkout), to your deposit first.
- **Defera Miles.** Your Miles can be applied at checkout to lower your price.
- **Airline travel credits and airline gift cards.** You can apply these at checkout under **Travel Credits & Gift Cards**. The amount comes off the fare right away and shows "Pending verification" until our team verifies it with the airline.

## How do I change my card later?

To change the card on file for a trip, tap **Update Card** on that trip, as described in [Updating your card](/help/payments/update-card).`,
  },
  {
    slug: 'card-declined',
    title: 'Why was my card declined?',
    summary: 'Cards are declined for everyday reasons, like a typo, an expired card, low funds or a bank hold. Here’s what to check, and how to get back on track.',
    keywords: ['payment declined', 'card not working', 'card failed', 'payment failed', 'card error', 'transaction declined', 'bank declined'],
    related: ['payments/update-card', 'payments/prepaid-cards', 'missed-payments/payment-failed', 'booking/booking-didnt-go-through'],
    updated: '2026-10-23',
    body: `A card can be declined for everyday reasons: a detail entered incorrectly, an expired card, not enough available funds or credit, or your bank stopping the charge. Prepaid and gift cards also aren't accepted on installment plans.

Start by checking your card details, then contact your bank, or add a different debit or credit card with **Update Card**. If a payment on your plan was declined, your trip isn't cancelled on the spot, and you can catch up as soon as it's sorted.

## What should I check first?

Go through these in order:

1. **Your card details.** The card number, expiration date, CVC and ZIP code must match the card exactly.
2. **The expiration date.** An expired card can't be charged. If your bank sent a replacement card, add it with **Update Card**, even if the number is the same.
3. **Your available funds.** Your account or credit line needs enough available to cover the payment.
4. **Your bank.** Banks sometimes decline a charge they don't expect, or ask you to approve it in their app or by text. Call the number on the back of your card, ask them to allow the payment, then try again.
5. **The card type.** Prepaid, gift and stored-value cards aren't accepted on installment plans, and the decline doesn't always say so. Use a regular debit or credit card instead; trying the same prepaid card again won't help.

## What if a payment on my plan was declined?

We know a declined payment can be worrying. Here's what happens, and how to get back on track:

1. **Your trip shows a past-due banner.** [A missed payment](/help/missed-payments/payment-failed) doesn't cancel your trip on the spot. The missed amount stays past due until it's paid, and it may be retried on your card.
2. **You sort out the card.** Contact your bank, or add a different card with **Update Card**, in the Payment Schedule section of your trip.
3. **You pay what's past due.** Tap **Make a Payment** and pay the past-due amount. You don't have to pay it all at once: the **Past due** tab takes any amount from $5 toward your oldest missed payment.
4. **Your plan carries on.** Once nothing is past due, your payments continue on their normal schedule.

## What if my new card won't save?

A failed card update changes nothing on your plan: the card already on file keeps being charged on schedule. Double-check the details, or try a different debit or credit card. If it's a prepaid card, it won't save, even if the message doesn't say why.

## What if checkout won't go through?

If you're booking a new trip and [checkout won't go through](/help/booking/booking-didnt-go-through), check your card details first. Before you try again, open **Trips** to see whether the booking went through, so you don't book twice.

If it still won't work, contact us so our team can take a look.

## How do I reach you?

[Chat with the Defera Concierge](/support), our AI concierge, any time. You can also call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) to reach the same AI concierge by voice, 24/7, or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'prepaid-cards',
    title: 'Can I use a prepaid or gift card?',
    summary: 'Not on a payment plan. Prepaid, gift and stored-value cards aren’t accepted on installment plans, so use a debit or credit card. Airline gift cards are different.',
    keywords: ['prepaid card', 'gift card', 'Visa gift card', 'stored value card', 'reloadable card', 'card not accepted'],
    related: ['payments/accepted-payment-methods', 'payments/update-card', 'payments/card-declined'],
    updated: '2026-10-05',
    body: `Not on a payment plan. Prepaid cards, including gift cards and other stored-value cards, aren't accepted on Defera's installment plans. Use any debit or credit card instead, including the debit card your bank issued you.

Airline gift cards are a different story. You can apply one at checkout to lower your fare, and we explain how below.

## What counts as a prepaid card?

A prepaid card is loaded with money ahead of time, instead of drawing on a bank account or a line of credit. Common examples include:

- gift cards from a card network, like the Visa or Mastercard gift cards sold in stores;
- reloadable prepaid cards;
- other stored-value cards.

Not sure whether your card is prepaid? The bank or company that issued it can tell you.

## Why does my card keep failing?

When a prepaid card is declined, the message doesn't always say why. So if a card keeps failing at checkout or on **Update Card**, and it's a prepaid or gift card, that's the reason.

Switch to a regular debit or credit card. Trying the same card again won't change the result.

## Is my plan affected?

No. If you tried to switch an existing trip to a prepaid card and it didn't save, nothing changed on your plan. The card already on file keeps being charged on schedule.

To change it, [update your card](/help/payments/update-card) on your trip with a debit or credit card, using **Update Card**.

## How do airline gift cards work?

At checkout, under **Travel Credits & Gift Cards**, you can apply:

- an airline gift card;
- an airline travel credit or eCredit;
- a certificate or voucher.

The amount comes off the fare right away. It shows "Pending verification" until our team verifies it with the airline.

Any payments left on your plan still need a debit or credit card on file. So an airline gift card lowers your price, and your card on file covers the payments that remain.

## Not sure a card will work?

[Chat with the Defera Concierge](/support), our AI concierge, and it can help you check. For everything we accept, see [What payment methods can I use?](/help/payments/accepted-payment-methods)`,
  },
  {
    slug: 'payment-not-showing',
    title: 'I made a payment but my balance didn’t change',
    summary: 'A payment can count without changing the number you’re watching. Here’s how each kind of payment and Defera Credit show up, and when to ask us.',
    keywords: ['payment not showing', 'balance not updated', 'payment missing', 'paid but still owe', 'balance wrong', 'payment not applied', 'credit not applied'],
    related: ['payments/balance-explained', 'payments/make-a-payment', 'payments/refund-timing', 'changes-cancellations/where-is-my-credit'],
    updated: '2026-10-23',
    body: `If your balance looks the same after a payment, your payment may have changed something else. Depending on how you paid, it can move your next charge date back, or make your future payments smaller. And if Defera Credit is applied to your trip, it sits beside your balance and covers your upcoming payments, so your balance can stay the same even as those payments drop to $0.

We know it's unsettling when a number doesn't move the way you expected. Below, we walk through how each kind of payment shows up, and how Defera Credit fits in. If it still doesn't add up, our team can check it for you.

## Am I looking at the right trip?

1. Open **Trips** and tap the trip you paid on. Each trip has its own payment schedule, so make sure you're looking at the right one.
2. Look at the Payment Schedule section. If you paid a moment ago, refresh the page.
3. For a full history of your plan, including paid, upcoming and any past-due installments, download your Trip Statement from the download icon in your trip's header row.

## How does each kind of payment show up?

Each option in **Make a Payment** changes your schedule in a different way:

| How you paid | What you'll see afterward |
|---|---|
| **Pay ahead** | Your next one or more installments are covered, and your next automatic charge moves back |
| **Principal** | Your future installments get smaller, and the dates stay the same. Your next payment is still due on the same day, for less |
| **Past due** | Your payment goes to your oldest missed payment first. If you paid part of it, the rest stays past due until it's paid |

So after a Principal payment, look at the amounts of your upcoming payments, not their dates. After Pay ahead, look at the date of your next charge.

## How do Defera Credit and my balance fit together?

This is the part that confuses people most, so let's take it one step at a time. Defera Credit is credit on your Defera account that you can put toward trips with us. Here's what happens when it's applied to a trip:

1. **The credit is set aside for your trip.** At checkout, it applies automatically, to your deposit first and then to your upcoming payments. Credit you receive after booking is added with **Apply Credits** on your trip's payment schedule, and it's spread across your upcoming payments, starting with the next one.
2. **It leaves your Available credit.** On your [Profile](/profile), the Defera Credits card shows your "Available credit". Credit applied to a trip leaves that figure, so it can show $0. The credit isn't missing: it's working on that trip.
3. **It covers your payments in order, earliest first.** The payments it covers drop to $0, so your card isn't charged for them. Your card is charged again from the first payment the credit doesn't cover.
4. **It sits beside your balance.** Your remaining balance can still include the payments your credit is covering. That's why **Pay off** charges only what's left after the credit, and the credit covers the rest.

Here's an example, with simple illustrative numbers:

- Your trip has $600 left to pay, in weekly payments of $100.
- You apply $200 of Defera Credit with **Apply Credits**.
- The credit covers your next two payments, so your card is charged $0 for those two weeks, and it's charged again from the third payment.
- Your Available credit goes down by $200.
- If you tap **Pay off** today, you're charged $400, and the credit covers the other $200.

## What if my credit arrived after I booked?

New Defera Credit doesn't reach a trip you've already booked on its own. That's why your next payment can still show its full amount, even though you have credit. To use it, open the trip and tap **Apply Credits** on its payment schedule. It's spread across your upcoming payments, starting with the next one.

## What if I'm waiting on a refund?

If you're expecting money back, rather than looking for a payment, that's a refund, and it works differently. Card refunds typically take 5–10 business days to appear, because your bank controls [when a refund shows up](/help/payments/refund-timing).

## Still doesn't add up?

If your balance still looks wrong, our team can check your payments for you. [Chat with the Defera Concierge](/support), our AI concierge, and it can send a request to our team. Have the date and amount of your payment handy.

Your request shows as a card on your trip page, and the answer posts there and comes by email, usually within 24 hours. You can also send us a message from the [Contact page](/contact#message).`,
  },
  {
    slug: 'balance-explained',
    title: 'Understanding your balance',
    summary: 'Your balance is what’s left of your trip total after what you’ve paid. Here’s what’s in it, and how Defera Credit on your trip sits beside it.',
    keywords: ['remaining balance', 'how much do I owe', 'trip total', 'amount paid', 'balance breakdown', 'what I owe', 'credit applied'],
    related: ['payments/payment-not-showing', 'payments/fees-explained', 'payments/trip-statement', 'changes-cancellations/use-defera-credit'],
    updated: '2026-10-05',
    body: `Your remaining balance is what's left of your trip total after your deposit and the payments you've made so far. It goes down with each payment you make, until your trip is paid in full.

Your trip total already includes the fare with taxes, plus Defera's service fee and hold fee, with no interest added. And if Defera Credit is applied to your trip, it sits beside your balance and covers your payments in order, earliest first. We'll walk through how that works below.

## What do the numbers on my trip mean?

- **Trip total:** the full price of your trip. It includes the fare with airline taxes and carrier fees, the service fee, the hold fee and any seats you chose at checkout.
- **Deposit:** what you paid when you booked. On Defera Zero, it's $0.
- **Installments:** the weekly or bi-weekly payments charged to your card on file.
- **Remaining balance:** your trip total minus everything you've paid so far.
- **Defera Credit applied:** credit from your Defera account that's set aside for your trip. It's used on your payments in order, earliest first.

For example (illustrative amounts), if your trip total is $1,000 and you've paid $400 so far, deposit included, your remaining balance is $600.

## How does Defera Credit fit with my balance?

Defera Credit is credit on your Defera account that you can put toward trips with us. Here's what happens, step by step, when it's applied to your trip:

1. **The credit is set aside for your trip.** At checkout, it applies automatically, to your deposit first and then to your upcoming payments. Credit you add later with **Apply Credits** is spread across your upcoming payments, starting with the next one.
2. **It leaves your Available credit.** The Defera Credits card on your [Profile](/profile) shows your "Available credit". Credit set aside for a trip leaves that figure, so $0 there means your credit is working on that trip, not missing.
3. **It covers your payments in order, earliest first.** Those payments can drop to $0. Your card is charged again from the first payment the credit doesn't cover.
4. **It sits beside your balance.** Your remaining balance can still include the payments your credit will cover. So **Pay off** charges only what's left after the credit, and the credit covers the rest.

For example (illustrative amounts), say your trip has a $600 remaining balance, with weekly payments of $100, and $200 of Defera Credit is applied to it. The credit covers your next two payments, so they show $0. If you paid off your trip today, you'd be charged $400, and the credit would cover the other $200.

## Why can my numbers look different from what I expect?

- **Credit that arrived after you booked.** It doesn't apply to an existing trip on its own. Tap **Apply Credits** on your trip's payment schedule to spread it across your upcoming payments.
- **Extra payments.** A **Principal** payment lowers your future installments without moving the dates. **Pay ahead** moves your next charge back.
- **Schedule changes.** **Change Schedule** re-spreads your balance across weekly or bi-weekly payments. **Skip Payment** makes your next installment $0 and raises the rest slightly.
- **Seats and bags bought after booking.** These are paid in full by card at the time, separately from your payment plan.
- **Airline credits and Miles.** Airline travel credits applied at checkout come off the fare, and your price breakdown shows them as "Credits Applied". Defera Miles applied at checkout lower the price before your plan is built, so your deposit and every payment are smaller.
- **Flight + Hotel trips.** The hotel may collect some fees at check-in. Those are labeled on your trip.

## Where can I see the details?

Before you booked, the **Price Breakdown** on the checkout page itemized your trip total. After booking, your [Trip Statement](/help/payments/trip-statement) carries your trip's pricing and payment history. You can download it from the download icon in your trip's header row.

## Think something's wrong?

If you believe your total or balance is wrong, not only unclear, [chat with the Defera Concierge](/support), our AI concierge. It can send a request to our team to review it. Your request shows as a card on your trip page, and the answer posts there and comes by email. [How requests to our team work](/help/contact/how-requests-work) explains what happens next.`,
  },
  {
    slug: 'trip-statement',
    title: 'Downloading your Trip Statement',
    summary: 'Every trip has a Trip Statement: a PDF with your itinerary, travelers, pricing and payment history. Download it from the icon in your trip’s header row.',
    keywords: ['receipt', 'itinerary', 'invoice', 'PDF', 'printout', 'proof of travel', 'booking statement', 'visa itinerary'],
    related: ['payments/view-payment-schedule', 'confirmation-and-travel/proof-of-onward-travel', 'confirmation-and-travel/when-confirmation-number', 'emails/booking-confirmation-email'],
    updated: '2026-10-05',
    body: `Every trip has a Trip Statement: a print-ready PDF of your booking. To get it, open **Trips**, tap your trip, and tap the download icon in the trip's header row, next to the message and call support buttons.

It's available on every trip, at every status, and the file is named after your booking reference. It's the easiest way to keep a record of your trip, or to share your plans with someone else.

## How do I download it?

1. Open **Trips** and tap your trip.
2. Find the trip's header row, where the message and call support buttons are.
3. Tap the download icon next to them.

Your Trip Statement downloads as a PDF you can save, print or share.

## What's on it?

- **Your itinerary.** Airlines, flight numbers, and dates and times for every flight.
- **Your travelers.** Every passenger's name.
- **Your pricing.** The pricing summary for your trip.
- **Your payment plan.** Its full history: paid, upcoming and any past-due installments.
- **Your reference codes.** Your Defera booking reference, and your airline confirmation number once your trip is paid in full.

## Booking reference or confirmation number?

Your booking reference is the code Defera uses to find your trip. It's at the top of your booking confirmation email, and it's what your Trip Statement shows while you're still paying.

Your airline confirmation number is the code the airline uses to find your reservation, and you'll use it to check in. It appears on your Trip Statement once your trip is paid in full, at the same moment it's released in the app. So if you download a fresh copy after your confirmation number is released, you'll see it there.

## When does it come in handy?

- **A receipt or printout.** A record of your trip and what you've paid.
- **An itinerary to share.** Send the PDF to a travel companion, a family member or anyone else yourself.
- **Proof of upcoming travel.** For example, for a visa application or an onward-travel check. It shows a confirmed booking with your full itinerary and passenger names. Whether a particular authority accepts it is up to that authority.
- **Paperwork for work.** A document for your employer, or for an expense reimbursement.

## Is it a boarding pass?

No. Your Trip Statement is a booking statement, not a boarding pass. Once your confirmation number is released, you check in with the airline as usual.

## Can you send it to a different email address?

We can't send trip documents to an email address that isn't on your account. You don't need us to, though: download your Trip Statement and send it to anyone you like.`,
  },
  {
    slug: 'fees-explained',
    title: 'Fees, explained',
    summary: 'Your trip total already includes the service fee and hold fee, with no interest. A few optional services have a fee, and you pay it only if you use them.',
    keywords: ['service fee', 'hold fee', 'hidden fees', 'interest', 'skip fee', 'extension fee', 'processing fee', 'charges'],
    related: ['getting-started/what-you-pay', 'payments/skip-a-payment', 'payments/pick-your-dates', 'payments/balance-explained'],
    updated: '2026-10-05',
    body: `Your trip total already includes the fare with airline taxes and carrier fees, plus Defera's service fee and hold fee. There's no interest, and there are no hidden fees. The price you see at checkout is the price you pay.

A few optional services have their own fee, like skipping a payment. You pay one of those only if you choose that service.

## What's in my trip total?

Two Defera fees are built into every trip total:

- **Service fee:** covers dedicated support throughout your payment plan.
- **Hold fee:** covers paying for your trip over time, instead of all at once.

Each one appears in the **Price Breakdown** on the checkout page, with a small info icon that gives a one-line explanation.

## What does the Price Breakdown show?

The Price Breakdown lists your trip's cost line by line, in this order:

1. Base fare, after any credits applied
2. Service fee
3. Seat selection, if you chose seats ("Free" when it's included)
4. Subtotal, before the hold fee
5. Hold fee
6. Trip total

It's open by default on your checkout page, and you can collapse it and open it again. After booking, your Trip Statement carries the pricing for your trip.

## Are prices shown with fees included?

Yes. Prices are shown all-in by default: fares include airline taxes and carrier fees, and the trip total includes the service and hold fees. So the price you see isn't a base price that grows later.

If you switch your search results to show base fares instead, those are labeled as a fare, not a total. Checkout always shows your full trip total.

The one exception is Flight + Hotel trips, where the hotel may collect some fees at check-in. Those are labeled on your trip.

## What optional fees are there?

These fees apply only if you choose the service:

| If you choose to | The fee is |
|---|---|
| Skip your next installment with **Skip Payment** | {{SKIP_FEE}}, charged to your card on file that day |
| Pick your own payment dates with **Pick my own dates** | {{DATE_CHANGE_FEE}}, charged to your card on file when you confirm; it covers every date change you make that day |
| Move a payment you can't make with **Can't make your payment?** | {{MOVE_FEE_SHORT}} for up to {{MOVE_FEE_SHORT_DAYS}} days later, {{MOVE_FEE_LONG}} for up to {{MOVE_MAX_DAYS}}; spread across your remaining payments, and not refunded if you cancel |
| Move your final payment date with **Request Extension**, on some older Defera Installments plans | {{EXTENSION_FEE}} |
| Reinstate a defaulted trip, on eligible bookings | {{REINSTATEMENT_FEE}}, charged to your card on file when our team processes it |
| Buy seats after booking with **Purchase Seats** | A small processing fee, shown as a dollar amount before you pay |

None of these is charged unless you use the service. The Skip Payment fee and the Extension Fee can't be waived.

For example, if you pick new payment dates in the morning and change them again that afternoon, you pay {{DATE_CHANGE_FEE}} once, not twice.

To ask about reinstating a trip, see [Options for a defaulted trip](/help/missed-payments/defaulted-trip-options).

## What's free?

- **Switching between weekly and bi-weekly** with **Change Schedule**: no fee, and nothing is charged today.
- **Paying ahead, paying extra or paying off early:** no fee and no penalty.
- **Updating your card** with **Update Card**.`,
  },
  {
    slug: 'unrecognized-charge',
    title: 'A charge I don’t recognize',
    summary: 'A Defera charge can be a payment, a fee for a service you chose or an add-on. Here’s how to match it to your trip, and what to do if it doesn’t fit.',
    keywords: ['unknown charge', 'unexpected charge', 'double charged', 'what is this charge', 'bank statement', 'extra charge', 'charged twice'],
    related: ['payments/view-payment-schedule', 'payments/trip-statement', 'payments/fees-explained', 'payments/refund-timing'],
    updated: '2026-10-23',
    body: `A charge from Defera can be a deposit, a scheduled payment, an extra payment, a fee for a service you chose, or an add-on such as seats or bags. The quickest way to identify it is to compare it with your trip's payment schedule and your Trip Statement.

We know an unfamiliar charge can be unsettling. If it still doesn't match anything after you've checked, contact us, and our team can take a look.

## How do I match a charge to my trip?

1. **Open each of your trips.** Open **Trips** and tap each trip you have with us. Each trip has its own payment schedule, so a charge might belong to a different trip than you expect.
2. **Check the payment schedule.** In the Payment Schedule section, look for a payment on the date of the charge, for the same amount.
3. **Check your Trip Statement.** For the full payment history, including paid, upcoming and any past-due installments, download your Trip Statement from the download icon in the trip's header row.

A charge can show as pending at your bank before it posts, so the date on your bank statement may not match your payment date exactly.

## What else could it be?

Besides your regular installments, these are the charges you might see:

| Charge | When it happens |
|---|---|
| Your deposit | When you booked |
| An extra payment | When you used **Make a Payment** to pay ahead, make a **Principal** payment or pay off |
| A past-due payment | When a missed payment is paid, or retried on your card |
| Skip Payment fee ({{SKIP_FEE}}) | The day you skipped a payment |
| Pick my own dates fee ({{DATE_CHANGE_FEE}}) | The day you confirmed new payment dates |
| Request Extension fee ({{EXTENSION_FEE}}) | If you extended your final payment date, on some older Defera Installments plans |
| Reinstatement fee ({{REINSTATEMENT_FEE}}) | When our team reinstated a defaulted trip |
| Seats bought after booking (**Purchase Seats**) | When you bought them, paid in full by card with a small processing fee |
| Checked bags bought after booking (**Purchase Baggage**) | When you bought them, paid by card in one go |

Seats and bags bought after booking are charged on their own, separately from your payment plan, so look for them by the date you bought them.

## What if a charge disappeared?

If you [cancelled within 24 hours of booking](/help/changes-cancellations/cancel-within-24-hours), your deposit is refunded in full to your card. It's reversed on the card itself, so most of the time the charge drops off your statement, usually within a day or so, rather than showing as a separate refund.

## What if I think I was charged twice?

First, check whether the two charges belong to two different trips, or are for two different things, like a payment and a fee. Also check whether one of them is still pending at your bank. If it still looks like the same charge twice, contact us so our team can check it.

## Still can't match it?

[Chat with the Defera Concierge](/support), our AI concierge, and it can send a request to our team to check the charge. It helps to include:

- the date and amount of the charge;
- your booking reference, if you have one.

You can also call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) to reach the same AI concierge by voice, 24/7, or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'refund-timing',
    title: 'When will my refund arrive?',
    summary: 'Card refunds typically take 5–10 business days to show up, and they go back to the original card. Defera Credit is instant: it posts as soon as it’s granted.',
    keywords: ['refund status', 'where is my refund', 'refund time', 'money back', 'refund to card', 'refund pending', 'business days'],
    related: ['changes-cancellations/cancel-within-24-hours', 'changes-cancellations/what-you-get-back', 'changes-cancellations/what-is-defera-credit', 'payments/unrecognized-charge'],
    updated: '2026-10-23',
    body: `Card refunds typically take 5–10 business days to show up, and sometimes sooner. The money leaves Defera right away, but your bank controls when it appears in your account.

Refunds go back to the original card you paid with. Defera Credit works differently: it posts to your Defera account the moment it's granted.

## Why does it take several days?

When we issue a refund to your card, the money leaves Defera immediately. From there, your bank processes it and decides when it appears on your statement. That's why it typically takes 5–10 business days, and sometimes it's faster.

Business days are weekdays, so weekends don't count. For example, if a refund is issued on a Monday, you can typically expect it between the following Monday and the Monday after that.

## How do the different kinds of money back compare?

Different kinds of money back arrive in different ways:

| What you're getting back | Where it goes | When you'll see it |
|---|---|---|
| A refund to your card | The original card | Typically 5–10 business days, sometimes sooner |
| Your deposit, after cancelling within 24 hours of booking | The card you paid with | Usually the charge disappears within a day or so; if it had already posted, within a few business days |
| Defera Credit | Your Defera account | Instantly |

The takeaway: Defera Credit is instant, a deposit reversed after a 24-hour cancellation is usually quick, and other card refunds depend on your bank.

## What if I cancelled within 24 hours of booking?

If you [cancel within 24 hours of booking](/help/changes-cancellations/cancel-within-24-hours), your deposit is refunded in full to the card you paid with, automatically. You don't need to ask for it.

It's reversed on the card itself, so most of the time the charge disappears from your statement rather than showing as a separate refund line. This is a card refund, not Defera Credit. If it hasn't come back after a few business days, contact us so our team can check the transaction.

## How do Defera Zero cash refunds work?

On Defera Zero, you can cancel up to 24 hours before departure and take 75% of your payments back as a refund to your card. The cancel screen shows the exact split and asks you to confirm, and the refund then goes through automatically. Like any card refund, it typically takes 5–10 business days to appear.

For example, if you've paid $400 on a Defera Zero trip (an example amount) and cancel more than 24 hours before departure, the cash option returns $300 to your card. [What you get back when you cancel](/help/changes-cancellations/what-you-get-back) covers all the options.

## How is Defera Credit different?

[Defera Credit](/help/changes-cancellations/what-is-defera-credit) posts to your account instantly, never expires and can be used toward any future trip, including trips for other people. It can't be turned into cash.

## What if my refund hasn't arrived?

1. **Check the original card.** Refunds can't be redirected to a different card, a bank account or another person, so look at the card you paid with.
2. **Ask your bank if that card changed.** If the card has been replaced or closed, your bank can tell you how it handles a refund sent to it.
3. **Contact us after 10 business days.** If it's been longer than that, [chat with the Defera Concierge](/support), our AI concierge, or send us a message from the [Contact page](/contact#message), so our team can check the transaction. Our team replies within 24 hours.`,
  },
];
